import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  ContextPanelContextValue,
  ContextPanelOpenOptions,
  ContextPanelScope,
} from './context-panel-context';
import { contextByScope } from './context-panel-context';

export interface ContextPanelProviderProps {
  children: ReactNode;
  /**
   * The scope this provider manages. Determines which context is provided.
   */
  scope: ContextPanelScope;
}

/**
 * Provides context panel state management for the given scope. Pre-placed in
 * `Layout` (global) and `Page` (page), or manually wrapped for component scope.
 */
export const ContextPanelProvider = ({
  children,
  scope,
}: ContextPanelProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ContextPanelOpenOptions | null>(null);

  const open = useCallback((openOptions: ContextPanelOpenOptions) => {
    setOptions(openOptions);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setOptions(null);
  }, []);

  const value = useMemo<ContextPanelContextValue>(
    () => ({ close, isOpen, open, options }),
    [close, isOpen, open, options],
  );

  const Context = contextByScope[scope];

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default ContextPanelProvider;
